import { entityToIndex } from '@squareup/dex-tech-utils-routing';
import { Link } from '@squareup/dex-ui';
import { Box, Paragraph10, Paragraph20 } from '@squareup/dex-ui-shared-base';
import { getWindow } from '@squareup/dex-utils-environment';
import clsx from 'clsx';
import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import styles from './quick-select.module.css';

interface LetterState {
  letter: string;
  disabled: boolean;
}

interface QuickSelectProps {
  letterStates: LetterState[];
  type: 'object' | 'enum';
  selectedIndex: string | undefined;
  getListElement: () => HTMLElement | null;
}

const SCROLLSPY_PADDING = 50;

function findScrolledIndex(
  getListElement: QuickSelectProps['getListElement'],
  setTargetIndex: (index: string | undefined) => void
) {
  let activeElement: Element | undefined;
  const observeElement = getListElement();

  if (!observeElement) {
    return;
  }

  for (
    let childIndex = 0;
    childIndex < observeElement.children.length;
    childIndex++
  ) {
    const childElement = observeElement.children[childIndex];

    const boundingRect = childElement?.getBoundingClientRect();

    if (boundingRect && boundingRect.top >= SCROLLSPY_PADDING) {
      // The current element is below the fold, meaning the
      // set "activeElement" is the actual active element.
      break;
    } else {
      // At this point, the current element can be assumed to be the
      // closer to "active" than the previous element.
      activeElement = childElement;
    }
  }

  setTargetIndex(activeElement ? activeElement.id : undefined);
}

const QuickSelect: FunctionComponent<QuickSelectProps> = ({
  letterStates,
  type,
  selectedIndex,
  getListElement,
}) => {
  const selectedIndexRef = useRef<string | undefined>(selectedIndex);
  const [currentIndex, setCurrentIndex] = useState<undefined | string>(
    undefined
  );

  const setIndex = useCallback(
    (index: undefined | string) => {
      selectedIndexRef.current = index;
      setCurrentIndex(selectedIndexRef.current);
    },
    [selectedIndexRef]
  );
  useEffect(() => {
    // Update the index on incoming prop change
    selectedIndexRef.current !== selectedIndex && setIndex(selectedIndex);
  }, [selectedIndex, setIndex]);

  useEffect(() => {
    const findScrolledIndexCallback = () =>
      findScrolledIndex(getListElement, (index: string | undefined) => {
        // Update the index on incoming scroll change
        setIndex(index);
      });

    const win = getWindow();
    win?.addEventListener('scroll', findScrolledIndexCallback, {
      passive: true,
    });

    return () => {
      win?.removeEventListener('scroll', findScrolledIndexCallback);
    };
  }, [getListElement, setIndex]);

  return (
    <Box margin={{ top: '4x' }}>
      <Paragraph20 as="span" weight="medium">
        Quick select
      </Paragraph20>
      <Box margin={{ bottom: '2x', top: '2x' }}>
        <ul data-testid="quick-select" className={styles.list}>
          {letterStates.map(({ letter, disabled }) => {
            const index = entityToIndex(type, letter);
            return (
              <li className={styles['list-item']} key={letter}>
                <Paragraph10
                  as="div"
                  className={styles['list-item-content']}
                  weight="semi-bold"
                >
                  <Link
                    trackingId={`navigate-to-reference-quick-selection-${letter}`}
                    className={clsx(
                      styles.link,
                      disabled && styles.disabled,
                      currentIndex === index && styles.active
                    )}
                    href={disabled ? undefined : `#${index}`}
                  >
                    {letter}
                  </Link>
                </Paragraph10>
              </li>
            );
          })}
        </ul>
      </Box>
    </Box>
  );
};

export { QuickSelect, type LetterState };
